// Defense in depth for an unfinished authored evidence/selection gate.
// The model is instructed to preserve discovery, but can still name an answer
// on repeated requests. This uses the learner-visible options, never an answer
// key, and changes only the displayed/spoken reply. It does not alter state.
const tidy = (value) => String(value || "").toLowerCase()
  .normalize("NFKC").replace(/[^\p{L}\p{N}]+/gu, " ").trim();

// This is presentation-only coaching. The authored phase decides the gate;
// recent *conversation* decides which non-revealing follow-up to use.
export const isSwanHelpRequest = (question) =>
  /\b(hint|stuck|help|clue|answer|which (one|option)|tell me|not sure|don.t know)\b/i.test(String(question || ""));

const GENERIC_STEPS = [
  "Review one clue in the current activity or experiment. What can you observe directly?",
  "Compare two clues you can see in the activity. What is similar or different?",
  "Tell me one thing you actually noticed, even if you're unsure. We'll work from that clue.",
];

// A word-for-word or nearly identical answer adds no help. Use a small,
// conservative word overlap check; ordinary different advice stays untouched.
const nearlySame = (a, b) => {
  const aa = tidy(a).split(" ").filter(Boolean);
  const bb = tidy(b).split(" ").filter(Boolean);
  if (aa.length < 6 || bb.length < 6) return tidy(a) === tidy(b) && !!tidy(a);
  const left = new Set(aa);
  const right = new Set(bb);
  const common = Array.from(left).filter((word) => right.has(word)).length;
  return common / Math.max(left.size, right.size) >= 0.78;
};

const fallback = (guard, hintAttempt = 1) => {
  // Steps are authored per investigation when available. The existing safe
  // hint is usually already visible as the purple "Keep investigating" note,
  // so don't repeat it as the conversation's first fallback.
  const steps = Array.isArray(guard?.hint_steps) && guard.hint_steps.length
    ? guard.hint_steps : GENERIC_STEPS;
  const index = Math.min(Math.max(1, Number(hintAttempt) || 1) - 1, steps.length - 1);
  const candidate = String(steps[index] || "").trim();
  if (candidate && !containsProtectedChoice(candidate, guard?.choice_labels)) return candidate;
  return GENERIC_STEPS[index % GENERIC_STEPS.length];
};

export const containsProtectedChoice = (answer, choiceLabels = []) => {
  const response = ` ${tidy(answer)} `;
  return (Array.isArray(choiceLabels) ? choiceLabels : []).some((label) => {
    const choice = tidy(label).replace(/^(the|an|a) /, "");
    // Very short words would flag ordinary conversation (yes/no).
    if (choice.length < 4) return false;
    return response.includes(` ${choice} `);
  });
};

export const guardSwanAnswer = (answer, inquiryGuard, question = "", coaching = {}) => {
  // Never suppress urgent physical safety instructions for a kit problem.
  if (/\b(smok(e|ing|es)|sparks?|burn(ing|t)?|overheat|hot wire|leak(ing|s)?|injur(y|ed)|shock|fire)\b/i.test(String(question))) {
    return String(answer || "");
  }
  const text = String(answer || "");
  if (!inquiryGuard?.protect_answer) return text;
  const hintAttempt = coaching?.hintAttempt || 1;
  const repeated = isSwanHelpRequest(question) && (
    nearlySame(text, coaching?.previousAnswer) ||
    nearlySame(text, inquiryGuard.safe_hint)
  );
  if (!containsProtectedChoice(text, inquiryGuard.choice_labels) && !repeated) return text;
  return fallback(inquiryGuard, hintAttempt);
};
